import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    Check,
    ChevronRight,
    FolderTree,
    GripVertical,
    Loader2,
    Pencil,
    Plus,
    Trash2,
    X,
} from 'lucide-react';
import { DocFiltersContext } from '../DocFiltersContext';
import {
    fetchCollection,
    saveDocSpaceCategory,
    deleteDocSpaceCategory,
} from '../../../services/api';

const CATEGORIES_URL = '/decoupled-api/taxonomy_term/doc_space_category/collection?display=decoupled';

function normalizeCategory(raw) {
    const d = raw?.data ?? raw;
    const rawParent = d.parentId;
    const parentId = Array.isArray(rawParent)
        ? (rawParent[0] ? parseInt(rawParent[0], 10) : 0)
        : (rawParent ? parseInt(rawParent, 10) : 0);
    return {
        id: d.id,
        name: d.name || '',
        parentId,
        weight: d.weight || 0,
        description: d.description || '',
    };
}

function buildTree(categories) {
    const roots = categories.filter((c) => !c.parentId || c.parentId === 0);
    const childrenMap = new Map();
    categories.forEach((c) => {
        if (c.parentId && c.parentId !== 0) {
            if (!childrenMap.has(c.parentId)) childrenMap.set(c.parentId, []);
            childrenMap.get(c.parentId).push(c);
        }
    });
    return { roots, childrenMap };
}

function DeleteConfirm({ categoryName, onConfirm, onCancel, loading }) {
    return (
        <div id="doc-settings-categories-delete-modal" className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} aria-hidden="true" />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle size={20} className="text-red-500" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-stone-900">Supprimer la catégorie</h3>
                        <p className="text-sm text-stone-500 mt-1">
                            Supprimer <strong className="text-stone-700">{categoryName}</strong> ? Les espaces assignés perdront cette catégorie.
                        </p>
                    </div>
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="btn btn-secondary"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        className="btn btn-danger disabled:opacity-50 flex items-center gap-2"
                    >
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        Supprimer
                    </button>
                </div>
            </div>
        </div>
    );
}

function CategoryRow({ cat, isChild, childCount, onEdit, onDelete, editingId, editState, setEditState, onEditSave, onEditCancel, savingId }) {
    const isEditing = editingId === cat.id;

    if (isEditing) {
        return (
            <div id={`doc-settings-category-edit-${cat.id}`} className={`px-4 py-3 bg-blue-50/40 ${isChild ? 'pl-10' : ''}`}>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                        type="text"
                        value={editState.name}
                        onChange={(e) => setEditState((s) => ({ ...s, name: e.target.value }))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') onEditSave();
                            if (e.key === 'Escape') onEditCancel();
                        }}
                        placeholder="Nom *"
                        className="input flex-1"
                        autoFocus
                    />
                    <input
                        type="text"
                        value={editState.description}
                        onChange={(e) => setEditState((s) => ({ ...s, description: e.target.value }))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') onEditSave();
                            if (e.key === 'Escape') onEditCancel();
                        }}
                        placeholder="Description (optionnel)"
                        className="input flex-1"
                    />
                    <div className="flex items-center gap-1.5 self-end sm:self-auto">
                        <button
                            type="button"
                            onClick={onEditSave}
                            disabled={savingId === cat.id || !editState.name.trim()}
                            className="btn btn-primary btn-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                            aria-label="Enregistrer"
                        >
                            {savingId === cat.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                            <span className="sm:hidden">Enregistrer</span>
                        </button>
                        <button
                            type="button"
                            onClick={onEditCancel}
                            className="p-2 rounded-lg text-stone-400 hover:bg-stone-200 hover:text-stone-600 transition-colors"
                            aria-label="Annuler"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id={`doc-settings-category-${cat.id}`} className={`flex items-center gap-2.5 px-4 py-3 group hover:bg-stone-50 transition-colors ${isChild ? 'pl-10' : ''}`}>
            <GripVertical size={14} className="text-stone-300 flex-shrink-0 hidden sm:block sm:opacity-0 sm:group-hover:opacity-100 transition-opacity cursor-grab" />

            {isChild && (
                <ChevronRight size={12} className="text-stone-300 flex-shrink-0 -ml-1" />
            )}

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-stone-800 font-medium truncate">{cat.name}</span>
                    {!isChild && childCount > 0 && (
                        <span className="text-2xs text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                            {childCount}
                        </span>
                    )}
                </div>
                {cat.description && (
                    <p className="text-xs text-stone-400 truncate mt-0.5">{cat.description}</p>
                )}
            </div>

            <div className="flex items-center gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                <button
                    type="button"
                    onClick={() => onEdit(cat)}
                    className="p-1.5 rounded-md text-stone-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    aria-label={`Modifier ${cat.name}`}
                >
                    <Pencil size={13} />
                </button>
                <button
                    type="button"
                    onClick={() => onDelete(cat)}
                    className="p-1.5 rounded-md text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    aria-label={`Supprimer ${cat.name}`}
                >
                    <Trash2 size={13} />
                </button>
            </div>
        </div>
    );
}

export default function DocSettingsCategories() {
    const filtersCtx = useContext(DocFiltersContext);

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [newName, setNewName] = useState('');
    const [newParentId, setNewParentId] = useState(0);
    const [newDescription, setNewDescription] = useState('');
    const [creating, setCreating] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editState, setEditState] = useState({ name: '', description: '' });
    const [savingId, setSavingId] = useState(null);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchCategories = useCallback(async () => {
        try {
            const raw = await fetchCollection(CATEGORIES_URL, true);
            const items = (Array.isArray(raw) ? raw : []).map(normalizeCategory);
            setCategories(items);
            filtersCtx?.setSpaceCategories(items);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [filtersCtx]);

    useEffect(() => {
        fetchCategories();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const { roots, childrenMap } = useMemo(() => buildTree(categories), [categories]);

    const rootOptions = useMemo(
        () => categories.filter((c) => !c.parentId || c.parentId === 0),
        [categories],
    );

    const handleCreate = useCallback(async () => {
        const trimmed = newName.trim();
        if (!trimmed) return;
        setCreating(true);
        setError('');
        try {
            await saveDocSpaceCategory({
                name: trimmed,
                parentId: newParentId || 0,
                description: newDescription.trim(),
            });
            setNewName('');
            setNewParentId(0);
            setNewDescription('');
            await fetchCategories();
        } catch (err) {
            setError(err.message);
        } finally {
            setCreating(false);
        }
    }, [newName, newParentId, newDescription, fetchCategories]);

    const handleEditStart = useCallback((cat) => {
        setEditingId(cat.id);
        setEditState({ name: cat.name, description: cat.description || '' });
    }, []);

    const handleEditSave = useCallback(async () => {
        if (!editingId) return;
        const trimmed = editState.name.trim();
        if (!trimmed) return;
        setSavingId(editingId);
        setError('');
        try {
            const cat = categories.find((c) => c.id === editingId);
            await saveDocSpaceCategory({
                id: editingId,
                name: trimmed,
                description: editState.description.trim(),
                parentId: cat?.parentId || 0,
            });
            setEditingId(null);
            await fetchCategories();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingId(null);
        }
    }, [editingId, editState, categories, fetchCategories]);

    const handleEditCancel = useCallback(() => {
        setEditingId(null);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setError('');
        try {
            await deleteDocSpaceCategory(deleteTarget.id);
            setDeleteTarget(null);
            await fetchCategories();
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleting(false);
        }
    }, [deleteTarget, fetchCategories]);

    if (loading) {
        return (
            <div id="doc-settings-categories" className="flex items-center justify-center py-16 gap-3">
                <Loader2 size={20} className="animate-spin text-stone-400" />
                <span className="text-sm text-stone-400">Chargement des catégories…</span>
            </div>
        );
    }

    return (
        <div id="doc-settings-categories">
            {/* Section description */}
            <div id="doc-settings-categories-intro" className="mb-6">
                <h2 className="text-lg font-semibold text-stone-900 mb-1">Catégories d'espaces</h2>
                <p className="text-sm text-stone-500">
                    Organisez vos espaces documentaires par catégories hiérarchiques. Les catégories servent au filtrage et à l'affichage de badges.
                </p>
            </div>

            {/* Error banner */}
            {error && (
                <div id="doc-settings-categories-error" className="alert alert-danger mb-4 items-center">
                    <AlertTriangle size={14} className="flex-shrink-0" />
                    <span className="flex-1">{error}</span>
                    <button type="button" onClick={() => setError('')} className="p-0.5 text-red-400 hover:text-red-600 flex-shrink-0" aria-label="Fermer l'erreur">
                        <X size={12} />
                    </button>
                </div>
            )}

            {/* Create form */}
            <div id="doc-settings-categories-form" className="bg-white border border-stone-200 rounded-xl mb-6">
                <div className="px-5 py-3.5 border-b border-stone-100 flex items-center gap-2">
                    <Plus size={14} className="text-stone-400" />
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Nouvelle catégorie</h3>
                </div>
                <div className="p-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        <div>
                            <label htmlFor="new-cat-name" className="block text-xs font-medium text-stone-500 mb-1.5">
                                Nom <span className="text-red-400">*</span>
                            </label>
                            <input
                                id="new-cat-name"
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                                placeholder="Ex : Projets, Processus…"
                                className="input"
                            />
                        </div>
                        <div>
                            <label htmlFor="new-cat-parent" className="block text-xs font-medium text-stone-500 mb-1.5">Parent</label>
                            <select
                                id="new-cat-parent"
                                value={newParentId}
                                onChange={(e) => setNewParentId(parseInt(e.target.value, 10) || 0)}
                                className="select"
                            >
                                <option value={0}>Racine (aucun parent)</option>
                                {rootOptions.map((r) => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="mb-4">
                        <label htmlFor="new-cat-desc" className="block text-xs font-medium text-stone-500 mb-1.5">Description</label>
                        <input
                            id="new-cat-desc"
                            type="text"
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                            placeholder="Description courte (optionnel)"
                            className="input"
                        />
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={handleCreate}
                            disabled={creating || !newName.trim()}
                            className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            <span>Ajouter la catégorie</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Category list */}
            <div id="doc-settings-categories-list" className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3.5 border-b border-stone-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FolderTree size={14} className="text-stone-400" />
                        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                            Catégories existantes
                        </span>
                    </div>
                    <span className="text-xs text-stone-400 tabular-nums">
                        {categories.length} élément{categories.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {categories.length === 0 ? (
                    <div id="doc-settings-categories-empty" className="px-6 py-14 text-center">
                        <FolderTree size={32} className="text-stone-200 mx-auto mb-3" />
                        <p className="text-sm text-stone-400 font-medium">Aucune catégorie créée</p>
                        <p className="text-xs text-stone-400 mt-1">Utilisez le formulaire ci-dessus pour commencer.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-stone-100">
                        {roots.map((root) => {
                            const children = childrenMap.get(root.id) || [];
                            return (
                                <div key={root.id}>
                                    <CategoryRow
                                        cat={root}
                                        isChild={false}
                                        childCount={children.length}
                                        onEdit={handleEditStart}
                                        onDelete={setDeleteTarget}
                                        editingId={editingId}
                                        editState={editState}
                                        setEditState={setEditState}
                                        onEditSave={handleEditSave}
                                        onEditCancel={handleEditCancel}
                                        savingId={savingId}
                                    />
                                    {children.map((child) => (
                                        <CategoryRow
                                            key={child.id}
                                            cat={child}
                                            isChild
                                            childCount={0}
                                            onEdit={handleEditStart}
                                            onDelete={setDeleteTarget}
                                            editingId={editingId}
                                            editState={editState}
                                            setEditState={setEditState}
                                            onEditSave={handleEditSave}
                                            onEditCancel={handleEditCancel}
                                            savingId={savingId}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {deleteTarget && (
                <DeleteConfirm
                    categoryName={deleteTarget.name}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
